/** Live state of one item in a library, as the change engine needs it. */
export interface IDriveItemRef {
  id: string;
  name: string;
  /** Undefined for the library root. */
  parentId?: string;
  isFolder: boolean;
  /** Folders only: number of direct children. */
  childCount?: number;
}

/**
 * The only write surface the change engine uses. Implemented over Microsoft Graph for
 * real libraries and in memory for tests. Every method works on one library.
 */
export interface IDriveWriter {
  getRoot(): Promise<IDriveItemRef>;
  /** Undefined when the item no longer exists. */
  getItem(itemId: string): Promise<IDriveItemRef | undefined>;
  /** Child of a folder by name (case-insensitive, like SharePoint). Undefined when absent. */
  getChild(parentId: string, name: string): Promise<IDriveItemRef | undefined>;
  /** Fails if an item with that name already exists. */
  createFolder(parentId: string, name: string): Promise<IDriveItemRef>;
  /** Move and/or rename. Fails, rather than overwriting, if the destination name is taken. */
  moveItem(itemId: string, newParentId: string | undefined, newName: string | undefined): Promise<IDriveItemRef>;
  /**
   * Deletes an item to the site's recycle bin. Used only to remove empty folders the app
   * itself created to hold quarantined items, when a batch is undone — never customer content.
   */
  deleteItem(itemId: string): Promise<void>;
}

export class NameConflictError extends Error {
  constructor(name: string) {
    super(`An item named "${name}" already exists in the destination folder.`);
    Object.setPrototypeOf(this, NameConflictError.prototype);
  }
}
